import React from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import { TextButton } from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { useWearableDevice } from 'journey/productDetails/components/ProductConfigurator/hooks';
import { PlanCard } from 'shared/components';
import { useQuery } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils';
import { useProductDetails } from '../../../../../../provider';
import { ProductDetailsActionTypes } from '../../../../../../reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  IfpEligibilityCheckSteps,
  InfoLayerStatus,
  PlanBundleOptions,
} from '../../../../../../typings';

const SelectedPlanDetailContainer = styled.div`
  max-width: 360px;
  position: relative;
`;

const ChangePlanButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  margin-bottom: 10px;
  ${({ theme: { media } }) => media.up('slim')} {
    margin-bottom: -20px;
  }
`;

const StyledTextButton = styled(TextButton)`
  ${({ theme: { palette } }) => css`
    color: ${palette.baseColors.lupin};
  `}
`;

const SelectedPlanTile = () => {
  const {
    dispatch,
    ifpEligibility: { isCheckRequired, selectedLineNumber },
    planBundleOption,
    planOffers,
    selectedDevice,
  } = useProductDetails();
  const router = useRouter();
  const { replaceQuery, query } = useQuery();
  const { isWearable } = useWearableDevice();

  const queryParameterPlanId = extractFirstValue(router.query.planId);
  const selectedDevicePlanId = selectedDevice?.defaultPlan?.id;

  const queryParameterDefaultPlan = planOffers?.find(
    ({ id }) => id === queryParameterPlanId,
  );
  const selectedDeviceDefaultPlan = planOffers?.find(
    ({ id }) => id === selectedDevicePlanId,
  );
  const selectedPlan =
    queryParameterDefaultPlan ?? selectedDeviceDefaultPlan ?? planOffers?.[0];

  if (!selectedPlan) {
    return null;
  }

  const selectedPlanTileRegionId = isWearable
    ? 'wearablePlanSelectedTab'
    : 'mobilePlanSelectedTab';

  const hasNoPlanIdInUrl =
    planBundleOption === PlanBundleOptions.ChooseNewPlan &&
    !queryParameterPlanId;

  const ineligiblePlanIdInUrl =
    planBundleOption === PlanBundleOptions.ChooseNewPlan &&
    queryParameterPlanId !== selectedPlan.id;

  if (hasNoPlanIdInUrl || ineligiblePlanIdInUrl) {
    replaceQuery({ ...query, planId: selectedPlan.id });
  }

  const handleInfoIconClick = (contentKey: string) => {
    dispatch({
      type: ProductDetailsActionTypes.SetInfoLayer,
      infoLayer: {
        contentKey,
        status: InfoLayerStatus.ContentFragment,
        isNested: false,
      },
    });
  };

  return (
    <LinkRegion regionId={selectedPlanTileRegionId}>
      <SelectedPlanDetailContainer>
        <ChangePlanButtonWrapper>
          {!isWearable ? (
            <StyledTextButton
              variant="textbutton"
              onClick={() => {
                dispatch({
                  type: ProductDetailsActionTypes.SetActionLayer,
                  actionLayerStatus: ActionLayerStatus.ShowNewPlan,
                  ifpEligibility: {
                    activeStep: IfpEligibilityCheckSteps.PlanSelection,
                    selectedLineNumber: isCheckRequired
                      ? selectedLineNumber
                      : 'NEW_PLAN',
                  },
                });
                trackCustomLink('Change plan');
              }}
            >
              Change plan
            </StyledTextButton>
          ) : null}
        </ChangePlanButtonWrapper>
        <PlanCard
          {...selectedPlan}
          badgeLabel="Selected"
          onInfoIconClick={handleInfoIconClick}
        />
      </SelectedPlanDetailContainer>
    </LinkRegion>
  );
};

export default SelectedPlanTile;
